import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import styles from "./AddUserModal.module.css";

import ModalActionBar from "shared/ModalActionBar";
import Button from "shared/Button";
import Input from "shared/@forms/Input";
import { useUsersList } from "../../../Permissions/Users/useUsersList";
import Loader from "shared/Loader";

import CloseButton from "../../assets/Close_Button.svg";
import CheckedIcon from "../../assets/Approved_Gray.svg";
import CheckboxItem from "ManagementTeam/Shared/CheckboxItem";

export function AddUserModal({ isOpen, onCollapse, onAdd, isLoading, error }) {
  const [errors, setErrors] = useState({ emails: "" });
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [accessLevels, setAccessLevels] = useState(["GUEST"]);
  const [iboNumber, setIBONumber] = useState("");

  const list = useUsersList();

  const users = useMemo(() => {
    if (list == null) {
      return null;
    }

    if (searchTerm.trim() === "") {
      return list;
    }

    const term = searchTerm.trim().toLowerCase();

    return list.filter(
      (user) =>
        user.displayName &&
        user.mail &&
        (user.displayName.toLowerCase().includes(term) ||
          (user.mail && user.mail.toLowerCase().includes(term)) ||
          (user.jobTitle && user.jobTitle.toLowerCase().includes(term)))
    );
  }, [list, searchTerm]);

  const handleAdd = () => {
    const emails = selectedUsers.map((a) => a.mail);

    if (!emails || emails.length == 0) {
      setErrors({ emails: "Please select recipient." });
      return;
    } else {
      setErrors({ emails: "" });
    }

    const user = selectedUsers[0];

    if (onAdd) {
      onAdd({
        user: {
          email: user.userPrincipalName,
          firstName: user.givenName,
          lastName: user.surname,
        },
        accessLevels,
        iboNumber,
      });
    }

    // Clear local state and close the modal after adding
    setSelectedUsers([]);
    setSearchTerm("");
    setIBONumber("");
    setAccessLevels(["GUEST"]);
    onCollapse && onCollapse();
  };

  const onClickUser = (e, user) => {
    e.stopPropagation();

    if (selectedUsers.includes(user)) {
      return;
    }

    setSelectedUsers((prev) => [
      ...prev.filter((x) => x.mail !== user.mail),
      user,
    ]);
    setSearchTerm("");
    setErrors({ emails: "" });
  };

  const onClickRemoveSelectedUser = (e, user) => {
    e.stopPropagation();

    setSelectedUsers((prev) => [...prev.filter((x) => x !== user)]);
  };

  return (
    <>
      {isOpen && (
        <div className={styles.overlay}>
          <div className={styles.outerContainer}>
            <div className={styles.buttons}>
              <ModalActionBar
                children={
                  <>
                    <Button
                      className={styles.cancel}
                      children="Cancel"
                      disabled={isLoading}
                      onClick={onCollapse}
                    />
                    <Button
                      className={classnames(styles.share, {
                        [styles.error]: error && error.error,
                      })}
                      children={isLoading ? "Adding..." : "Add"}
                      disabled={isLoading}
                      onClick={handleAdd}
                    />
                  </>
                }
              />
            </div>
            <div className={styles.innerContainer}>
              {selectedUsers.length == 0 && (
                <>
                  <label htmlFor="email">Enter Employee Email Address</label>
                  <Input
                    disabled={isLoading}
                    className={classnames(styles.input, {
                      [styles.error]: !!errors.emails,
                    })}
                    placeholder="Email Address"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </>
              )}
              <div className={styles.selectedUsersContainer}>
                {selectedUsers &&
                  selectedUsers.length > 0 &&
                  selectedUsers.map((user) => (
                    <div className={styles.selectedUserContainer}>
                      <img
                        className={styles.selectedUserCloseButton}
                        src={CloseButton}
                        alt="Remove"
                        onClick={(e) => onClickRemoveSelectedUser(e, user)}
                      />
                      <span className={styles.selectedUser}>{`${
                        user.givenName || user.displayName
                      } (${user.mail})`}</span>
                    </div>
                  ))}
              </div>
              {searchTerm && (
                <div className={styles.users}>
                  {!users ? (
                    <Loader />
                  ) : Array.isArray(users) && users.length === 0 ? (
                    <div className={styles.emptyResults}>No matching users</div>
                  ) : (
                    <ul className={styles.usersList}>
                      {users.map((user) => {
                        let isSelected = selectedUsers.includes(user);

                        return (
                          <li className={styles.userContainer} key={user.id}>
                            <div
                              className={classnames(styles.user, {
                                [styles.green]: isSelected,
                              })}
                              onClick={(e) => onClickUser(e, user)}
                            >
                              {user.displayName}
                              {user.jobTitle && (
                                <span> &mdash; {user.jobTitle}</span>
                              )}
                              <br />
                              {user.mail || user.userPrincipalName}
                            </div>
                            {isSelected && (
                              <img
                                className={styles.imgSelected}
                                alt="Selected"
                                width="25"
                                height="25"
                                src={CheckedIcon}
                              />
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              )}

              <label htmlFor="iboNumber">IBO Number</label>
              <Input
                disabled={isLoading}
                className={classnames(styles.input)}
                placeholder="IBO Number to associate the user with"
                value={iboNumber}
                onChange={(e) => setIBONumber(e.target.value)}
              />

              <label htmlFor="accessLevels">Access Levels</label>
              <div className={styles["fieldset-set"]}>
                {["GUEST", "EC"].map((addChoice, i) => (
                  <CheckboxItem
                    key={i}
                    label={addChoice}
                    disabled={addChoice == "GUEST"}
                    checked={accessLevels.includes(addChoice)}
                    onChange={() => {
                      setAccessLevels((prev) => {
                        return prev.includes(addChoice)
                          ? prev.filter((choice) => choice !== addChoice)
                          : [...prev, addChoice];
                      });
                    }}
                  />
                ))}
              </div>
              <div className={styles.accessHint}>Guest is required.</div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

AddUserModal.propTypes = {
  // Getter to determine if modal is displayed
  isOpen: PropTypes.bool,

  // Callback function for when the modal is closed
  onCollapse: PropTypes.func,

  // Function called when share is clicked
  onShare: PropTypes.func,
};
